import { discardParked, restoreParked } from '../data/draftRecovery';
import { shell } from '../app';
import { API_BASE } from '../config';
import { clearApiKey } from '../data/api';
import { discardFailed, listOutbox, restoreOps, retryFailed } from '../data/outbox';
import { getSyncStatus, onSyncStatus, runSync } from '../data/sync';
import { formatDateTime } from '../logic/dates';
import { syncHeadline } from '../logic/syncPill';
import { btn, confirmDialog, empty, section, segmented, linkBtn } from '../ui/components';
import { h, replaceChildren, span } from '../ui/dom';
import { navigate } from '../ui/router';
import type { View } from '../ui/router';
import { loadThemePref, setThemePref } from '../ui/theme';
import type { ThemePref } from '../ui/theme';

const THEME_OPTIONS: readonly { value: ThemePref; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

export const syncStatusView: View = ({ root }) => {
  shell.setChrome(true);
  shell.setTitle('Sync', { back: '#/home' });
  const host = h('div', { class: 'page page--sync' });
  root.appendChild(host);

  const render = async () => {
    const s = getSyncStatus();
    const ops = await listOutbox();
    const parked = ops.filter((o) => o.park_reason === 'conflict');
    const failed = ops.filter((o) => o.status === 'failed');
    const pending = ops.filter((o) => o.status === 'pending');
    const head = syncHeadline(s);
    replaceChildren(
      host,
      h(
        'div',
        { class: `card statuscard statuscard--${head.tone}` },
        h('div', { class: 'statuscard__state' }, span(`syncpill__dot`), ' ', head.text),
        s.message ? h('div', { class: 'statuscard__msg mono' }, s.message) : null,
        h(
          'div',
          { class: 'statuscard__meta mono' },
          `Last sync: ${s.lastSync ? formatDateTime(s.lastSync) : 'never'}`,
        ),
        h('div', { class: 'statuscard__meta mono' }, `API: ${API_BASE || 'same origin'}`),
        h(
          'div',
          { class: 'stack-h' },
          btn('Sync now', { variant: 'primary', onClick: () => void runSync() }),
          btn('Sign out', {
            variant: 'ghost',
            onClick: () => {
              if (
                !confirmDialog(
                  'Sign out? Queued changes stay on this phone until you sign in again.',
                )
              )
                return;
              clearApiKey();
              navigate('#/login', { replace: true });
            },
          }),
        ),
      ),
      section({ title: 'Draft recovery', count: ops.filter((o) => o.status === 'parked').length }, [
        ...ops
          .filter((o) => o.park_reason === 'dependency' && !o.existing_lead_id)
          .map((o) =>
            h(
              'div',
              { class: 'card' },
              h(
                'p',
                {},
                `${o.label}: waiting for a rejected change. Resolve it in Rejected by the server below.`,
              ),
            ),
          ),
        ...parked.map((o) =>
          h(
            'div',
            { class: 'card' },
            h('p', {}, 'Already exists — open existing lead / edit draft'),
            o.existing_lead_id
              ? linkBtn('Open existing lead', `#/leads/${encodeURIComponent(o.existing_lead_id)}`)
              : null,
            o.local_lead_id
              ? linkBtn('Edit draft', `#/leads/${encodeURIComponent(o.local_lead_id)}/edit`)
              : null,
            h(
              'p',
              {},
              'Clear or change the business binding to retry. Activity stays with this draft; it is never merged.',
            ),
            btn('Discard draft changes', {
              variant: 'danger',
              onClick: () => {
                if (
                  !o.local_lead_id ||
                  !confirmDialog(
                    'Discard this draft and all its dependent visits, objections and draft actions?',
                  )
                )
                  return;
                void discardParked(o.local_lead_id).then((dropped) => {
                  let undone = false;
                  shell.toast('Draft changes discarded — tap to undo', {
                    duration: 8000,
                    onClick: () => {
                      undone = true;
                      void restoreParked(dropped).then(render);
                    },
                  });
                  setTimeout(() => {
                    if (!undone) void runSync();
                  }, 8100);
                  void render();
                });
              },
            }),
          ),
        ),
      ]),
      section(
        { title: 'Waiting to sync', count: pending.length },
        pending.length
          ? pending.map((o) =>
              h(
                'div',
                { class: 'oprow' },
                span('oprow__label', o.label),
                span(
                  'oprow__meta mono',
                  `${o.method} · ${formatDateTime(o.created_at)}${o.attempts ? ` · ${o.attempts} tries` : ''}`,
                ),
              ),
            )
          : empty('Nothing queued.'),
      ),
      section(
        {
          title: 'Rejected by the server',
          count: failed.length,
          tone: failed.length ? 'danger' : undefined,
          action: failed.length
            ? h(
                'div',
                { class: 'stack-h' },
                btn('Retry', {
                  variant: 'subtle',
                  size: 'sm',
                  onClick: () => void retryFailed().then(() => runSync()),
                }),
                btn('Discard', {
                  variant: 'danger',
                  size: 'sm',
                  onClick: () =>
                    void discardFailed().then((dropped) => {
                      void runSync(); // the server version wins
                      shell.toast(
                        `Discarded ${dropped.length} change${dropped.length === 1 ? '' : 's'} — tap to undo`,
                        {
                          duration: 8000,
                          onClick: () => void restoreOps(dropped).then(() => runSync()),
                        },
                      );
                    }),
                }),
              )
            : undefined,
        },
        failed.length
          ? failed.map((o) =>
              h(
                'div',
                { class: 'oprow oprow--failed' },
                span('oprow__label', o.label),
                span('oprow__meta mono', o.last_error ?? 'rejected'),
              ),
            )
          : empty('No rejected changes.'),
      ),
      section(
        { title: 'Appearance' },
        segmented({
          options: THEME_OPTIONS,
          value: loadThemePref(),
          onChange: setThemePref,
          size: 'md',
          name: 'Appearance',
        }),
      ),
    );
  };

  void render();
  const off = onSyncStatus(() => void render());
  return off;
};
